import { Button, cn, Paragraph, PlusIcon, TagIcon } from '@quickmd-llc/design-system'
// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import React, { useCallback, useState, useMemo } from 'react'
import { AddICD10CodesModal } from './AddICD10CodesModal'
import '../styles.scss'

import { useQueryClient } from '@tanstack/react-query'
import type {
  VisitDoctorApiOutContract,
  IcdCodeStaffApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { IcdCodeChip } from './IcdCodeChip'

import { usePostVisitsVisitIdIcdcodes } from 'api/generated/visit/visit'
import {
  getGetVisitsVisitIdV2QueryKey,
  useGetVisitsVisitIdV2,
} from 'api/generated/visit-controller-v2/visit-controller-v2'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { errorStatus, reportIcd10Failure } from './icd10Diagnostics'

type Props = {
  hasError: boolean
  clearError: () => void
}
export const VisitICD10CodesView = ({ hasError, clearError }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { patientWithVisitInfo } = useDailyVisit()
  const queryClient = useQueryClient()
  const { data: visitDetails } = useGetVisitsVisitIdV2(
    patientWithVisitInfo?.VisitId || '',
    { includeMostRecentMatchingVisit: true },
    {
      query: { enabled: false },
    },
  )
  const currentIcdCodes = visitDetails?.IcdCodes
  const allSuggestedCodes = useMemo(() => {
    const suggested = visitDetails?.SuggestedIcdCodes ?? []
    const previousVisitCodes = visitDetails?.PreviousVisit?.IcdCodes ?? []
    const seenIds = new Set(suggested.map((c) => c.IcdCodeId))
    return [...suggested, ...previousVisitCodes.filter((c) => !seenIds.has(c.IcdCodeId))]
  }, [visitDetails?.SuggestedIcdCodes, visitDetails?.PreviousVisit?.IcdCodes])

  const suggestedIcdCodes = useMemo(
    () => allSuggestedCodes.filter((code) => !currentIcdCodes?.find((c) => c.IcdCodeId === code.IcdCodeId)),
    [allSuggestedCodes, currentIcdCodes],
  )
  const { mutateAsync: updateIcdCodes } = usePostVisitsVisitIdIcdcodes()

  const handleAddCodes = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleModalSubmit = useCallback(
    async (codes: IcdCodeStaffApiOutContract[], source: 'modal' | 'chip' = 'modal') => {
      const visitId = patientWithVisitInfo?.VisitId
      const counts = { codeCount: codes.length, missingIdCount: codes.filter((c) => !c.IcdCodeId).length, source }
      if (!visitId) {
        // The modal closes without waiting for this, so a dropped save would otherwise leave no trace (QMD-4885).
        reportIcd10Failure('submit-skipped-no-visit', counts)
        return
      }
      try {
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        await updateIcdCodes({ visitId, data: { IcdCodeIds: codes.map((c) => c.IcdCodeId) } })
        if (codes.length > 0 || hasError) {
          clearError()
        }
        queryClient.setQueryData<VisitDoctorApiOutContract | undefined>(
          getGetVisitsVisitIdV2QueryKey(visitId, { includeMostRecentMatchingVisit: true }),
          (prev) => (prev ? { ...prev, IcdCodes: codes } : prev),
        )
        setIsModalOpen(false)
      } catch (error) {
        console.error('Failed to update ICD codes', error)
        // console is stubbed out in production, so this report is the only trace a failed save leaves.
        reportIcd10Failure('submit-failed', { ...counts, status: errorStatus(error) })
      }
    },
    [patientWithVisitInfo?.VisitId, updateIcdCodes, queryClient, clearError, hasError],
  )
  function handleCodeToggle(item: IcdCodeStaffApiOutContract): void {
    const isFromSuggested = !!suggestedIcdCodes?.find((c) => c.IcdCodeId === item.IcdCodeId)
    if (isFromSuggested && !currentIcdCodes?.find((c) => c.IcdCodeId === item.IcdCodeId)) {
      tracking.trackEvent(PROVIDER_EVENT_NAMES.ADD_ICD_10_CODE_FROM_SUGGESTED, {
        PatientId: patientWithVisitInfo?.PatientId || '',
        PatientMrn: patientWithVisitInfo?.MedicalRecordNumber || '',
        ServiceTypeName: visitDetails?.ServiceTypeName || '',
        VisitId: patientWithVisitInfo?.VisitId || '',
        VisitTypeName: patientWithVisitInfo?.VisitName || '',
        IcdCodeId: item.IcdCodeId || '',
        IcdCode: item.Code || '',
        IcdCodeDescription: item.Description || '',
      })
    }
    if (currentIcdCodes?.find((c) => c.IcdCodeId === item.IcdCodeId)) {
      handleModalSubmit(
        currentIcdCodes.filter((c) => c.IcdCodeId !== item.IcdCodeId),
        'chip',
      )
    } else {
      handleModalSubmit([...(currentIcdCodes || []), item], 'chip')
    }
  }

  return (
    <div
      className={cn(
        'flex h-max w-full flex-col gap-sp-2 border-b border-border-medium bg-white p-sp-2',
        hasError && 'border-text-error',
      )}>
      <div className='flex h-sp-4 justify-between gap-sp-1'>
        <div className='flex items-center gap-sp-0.5'>
          <TagIcon iconSize='m' color={hasError ? 'text-text-error' : 'text-text-dark'} />
          <Paragraph className={cn('font-semibold', hasError ? 'text-text-error' : 'text-text-dark')}>
            ICD-10 codes
          </Paragraph>
        </div>
        <Button size='s' priority='secondary' onClick={handleAddCodes}>
          <PlusIcon /> Add
        </Button>
      </div>
      {hasError ?
        <Paragraph className='text-text-error'>Enter an ICD-10 code to complete this visit.</Paragraph>
      : <div className='space-y-sp-1'>
          {currentIcdCodes?.map((item) => (
            <IcdCodeChip key={item.IcdCodeId} code={item} variant='current' onClick={() => handleCodeToggle(item)} />
          ))}
        </div>
      }
      {suggestedIcdCodes?.length > 0 && (
        <div className='flex flex-col gap-sp-2'>
          <Paragraph className='font-semibold text-text-light' size='sm'>
            Suggested codes
          </Paragraph>
          <div className='space-y-sp-1'>
            {suggestedIcdCodes?.map((item) => (
              <IcdCodeChip
                key={item.IcdCodeId}
                code={item}
                variant='suggested'
                onClick={() => handleCodeToggle(item)}
              />
            ))}
          </div>
        </div>
      )}
      {isModalOpen ?
        <AddICD10CodesModal
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
          currentIcdCodes={currentIcdCodes || []}
          suggestedIcdCodes={allSuggestedCodes || []}
        />
      : null}
    </div>
  )
}
